// src/components/hub/MasteryZone.jsx
import React from 'react';
import { GraduationCap, BookOpen, Lock, Zap, AlertCircle } from 'lucide-react';

export default function MasteryZone({ 
  unit, 
  unlockedContent, 
  isUnitFinished, 
  isTeacherMode, 
  totalSets, 
  completedCount, 
  startSet, 
  handleUnlockClick,
  SPECIAL_COST,
  // ✅ NUEVAS PROPS PARA DETECTAR MÓDULO
  isModuleLevel = false, // Indica si es módulo completo o unidad
  moduleTitle = null,    // Título del módulo (para el copy)
  totalUnits = 0,        // Total de unidades del módulo
  completedUnits = 0     // Unidades completadas del módulo
}) {
  if (!unit) return null;

  // ✅ DETECTAR AUTOMÁTICAMENTE SI ES MÓDULO O UNIDAD
  const isModule = isModuleLevel || unit.id?.endsWith('-final-reading') || unit.id?.endsWith('-final-review');
  
  const remaining = isModule 
    ? Math.max(totalUnits - completedUnits, 0)  // Para módulo: unidades restantes
    : Math.max(totalSets - completedCount, 0);   // Para unidad: sets restantes
  
  const isComplete = isModule ? completedUnits === totalUnits : isUnitFinished;
  const contextTitle = isModule ? moduleTitle : unit.title;

  return (
    <div className="pt-6 border-t-2 border-slate-200 border-dashed px-6 max-w-lg mx-auto space-y-6">
      {/* Header con badge */}
      <div className="flex items-center justify-between">
        <h3 className="text-slate-400 font-black uppercase tracking-widest text-sm">
          {isModule ? 'Desafíos del Módulo' : 'Desafíos Finales'}
        </h3>
        {!isComplete && (
          <div className="flex items-center gap-1.5 bg-amber-100 text-amber-600 text-[10px] font-black px-2.5 py-1 rounded-full">
            <AlertCircle size={12} />
            <span>
              {remaining} {isModule ? 'unidades' : 'sets'} restantes
            </span>
          </div>
        )}
      </div>

      <div className="grid gap-4">
  {/* UNIT/MODULE REVIEW CARD */}
  {unit.reviewSet && (
    <ChallengeCard
      title={isModule ? "Module Review" : "Unit Review"}
      subtitle={isModule ? "Examen Final del Módulo" : "Examen Final"}
      icon={<GraduationCap size={24} />}
      color="purple"
      isUnlocked={isComplete || unlockedContent.includes(unit.reviewSet.id || `${unit.id}_review`) || isTeacherMode}
      onStart={() => startSet({ ...unit.reviewSet, isBossMode: true })}
      onUnlock={() => handleUnlockClick(
        unit.reviewSet.id || `${unit.id}_review`,
        isModule ? "Module Review" : "Unit Review",
        SPECIAL_COST  // ✅ PASAR COST COMO TERCER PARÁMETRO
      )}
      cost={SPECIAL_COST}
      remaining={remaining}
      totalSets={isModule ? totalUnits : totalSets}
      completedCount={isModule ? completedUnits : completedCount}
      contextTitle={contextTitle}
      isModule={isModule}
    />
  )}

  {/* UNIT/MODULE READING CARD */}
  {unit.readingSet && (
    <ChallengeCard
      title={isModule ? "Module Reading" : "Reading"}
      subtitle={isModule ? "Historia Extendida" : "Historia Interactiva"}
      icon={<BookOpen size={24} />}
      color="blue"
      isUnlocked={isComplete || unlockedContent.includes(unit.readingSet.id || `${unit.id}_reading`) || isTeacherMode}
      onStart={() => startSet(unit.readingSet)}
      onUnlock={() => handleUnlockClick(
        unit.readingSet.id || `${unit.id}_reading`,
        isModule ? "Module Reading" : "Reading Practice",
        SPECIAL_COST  // ✅ PASAR COST COMO TERCER PARÁMETRO
      )}
      cost={SPECIAL_COST}
      remaining={remaining}
      totalSets={isModule ? totalUnits : totalSets}
      completedCount={isModule ? completedUnits : completedCount}
      contextTitle={contextTitle}
      isModule={isModule}
    />
  )}
</div>

    </div>
  );
}

// ========== SUB-COMPONENTE DE TARJETA PREMIUM ==========
function ChallengeCard({ 
  title, 
  subtitle, 
  icon, 
  color, 
  isUnlocked, 
  onStart, 
  onUnlock, 
  cost, 
  remaining,
  totalSets,
  completedCount,
  contextTitle,
  isModule = false  // ✅ Nuevo prop
}) {
  const progressPercent = (completedCount / totalSets) * 100;

  // Colores desbloqueado
  const unlockedBg = color === 'purple' 
    ? 'bg-gradient-to-br from-purple-50 via-purple-50 to-indigo-50' 
    : 'bg-gradient-to-br from-blue-50 via-blue-50 to-cyan-50';
  
  const unlockedBorder = color === 'purple' ? 'border-purple-200' : 'border-blue-200';
  
  const iconBg = color === 'purple' 
    ? 'bg-gradient-to-br from-purple-500 to-indigo-600' 
    : 'bg-gradient-to-br from-blue-500 to-cyan-600';

  const buttonBg = color === 'purple'
    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 border-purple-800'
    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 border-blue-800';

  return (
    <div className={`relative rounded-3xl overflow-hidden transition-all duration-300 ${
      isUnlocked 
        ? `${unlockedBg} border-2 ${unlockedBorder} shadow-lg hover:shadow-xl hover:-translate-y-1`
        : 'bg-slate-100/80 border-2 border-dashed border-slate-200 opacity-90'
    }`}>
      
      {/* CONTENIDO */}
      <div className="p-5 space-y-4">
        
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Icono con círculo degradado */}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${
              isUnlocked 
                ? `${iconBg} text-white` 
                : 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-500'
            }`}>
              {isUnlocked ? icon : <Lock size={22} />}
            </div>
            
            <div className="text-left">
              {/* Badge de estado */}
              {!isUnlocked && (
                <p className="text-[9px] font-black uppercase text-slate-400 tracking-wider mb-0.5">
                  🔒 Bloqueado · Requisito previo
                </p>
              )}
              <h3 className={`font-black text-base md:text-lg leading-tight ${
                isUnlocked ? 'text-slate-800' : 'text-slate-500'
              }`}>
                {title}
              </h3>
              <p className="text-[11px] font-bold text-slate-400">{subtitle}</p>
            </div>
          </div>

          {/* BOTÓN (solo si está desbloqueado) */}
          {isUnlocked && (
            <button 
              onClick={onStart} 
              className={`${buttonBg} text-white px-5 py-2.5 rounded-xl font-black text-sm shadow-lg border-b-4 active:border-b-0 active:translate-y-1 transition-all`}
            >
              {color === 'purple' ? 'INICIAR' : 'LEER'}
            </button>
          )}
        </div>

        {/* BLOQUE DE REQUISITO (solo si está bloqueado) */}
        {!isUnlocked && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-slate-200 space-y-3">
            
            {/* ✅ Copy del requisito ADAPTADO según nivel */}
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                Requisito para desbloquear
              </p>
              <p className="text-xs font-bold text-slate-600">
                {isModule ? (
                  <>
                    Completa <span className="text-indigo-600 font-black">todas las {totalSets} unidades</span> del módulo <span className="font-black">"{contextTitle}"</span>.
                  </>
                ) : (
                  <>
                    Completa <span className="text-indigo-600 font-black">{remaining} {remaining === 1 ? 'set' : 'sets'}</span> más en <span className="font-black">"{contextTitle}"</span>.
                  </>
                )}
              </p>
            </div>

            {/* Barra de progreso mini */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center text-[10px] font-black text-slate-400">
                <span>{isModule ? 'Progreso del módulo' : 'Progreso actual'}</span>
                <span className="text-indigo-600">{completedCount} / {totalSets}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Botón de desbloqueo por XP */}
            <div className="pt-2 flex justify-end">
              <button
                onClick={onUnlock}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md border-2 border-amber-200 hover:scale-105 active:scale-95 transition-all"
              >
                <Zap size={14} fill="currentColor" />
                <span>Desbloquear por {cost} XP</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
